import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, FilePlus2, Send, Signature, Route, Lock, Archive, Plug, ShieldCheck } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import { useLocalizedUrl } from '../utils/urlHelper';
import { PageHero, SectionIntro, ClosingCta, CheckList, usePrimaryCta } from '../components/common/B2B';
import ClientLogos from '../components/common/ClientLogos.jsx';

// Plataforma (nov-2026), contenido diseñado desde cero: el ciclo de vida del
// documento en una sola sección (sin los desplegables de antes), todos los
// documentos, el Archivo de documentos de otras empresas, cómo se adapta a la
// operativa, integración y garantías, y el acompañamiento. No repite lo que ya
// cuentan las páginas de cada documento. Hero con foto (oficina de tráfico,
// GPT Image 2: marketing/prompts-imagenes/hero-plataforma-oficina-trafico.txt):
// las capturas reales disponibles son del ADR y no se enseñan (pioneros).
// Foto rehecha (4-nov) sin teléfono en la mesa («el monitor se mezclaba con el
// teléfono»), con la persona y los monitores en el 40 % derecho para que el
// texto no los pise; marcas de los marcos borradas. Encuadre 53 %: a 1440 px el
// pelo empieza donde acaba el texto y el monitor derecho se corta un poco.
// Fuera el enfoque «Plataforma DeCA» del título.
//
// Textos ampliados (Jesús, 4-nov) con la hoja de producto vigente
// (marketing/hoja-producto-bluecmr.html), sin volcarla entera. La .md está caducada.
// Recortada después el mismo 4-nov (Jesús veía la web «demasiado extensa»): listas
// de 2-3 puntos por paso, «A su medida» en 4 y el acompañamiento en el cierre.

const HERO_IMAGE = { src: '/images/plataforma/hero-oficina-trafico.webp', position: '53% 50%' };
const PAGE_URL = { es: 'https://www.bluecmr.com/plataforma', en: 'https://www.bluecmr.com/en/platform' };

const STEP_ICONS = [FilePlus2, Send, Signature, Route, Lock, Archive];

const COPY = {
  es: {
    seoTitle: 'Plataforma de documentos de transporte digitales | BlueCMR',
    seoDescription: 'Cómo gestiona BlueCMR sus documentos de transporte: creación, envío, firma electrónica, eventos en ruta, cierre y conservación con evidencias.',
    seoKeywords: 'plataforma documentos de transporte, ecmr, carta de porte digital, deca, firma electrónica transporte, integración tms erp',
    heroEyebrow: 'Plataforma',
    heroTitle: 'Una plataforma para todos los documentos de sus transportes',
    heroText: 'Emita, comparta, firme y conserve eCMR, cartas de porte, DeCA y documentos ADR desde un mismo sitio, conectado con su TMS o ERP y con quien participa en cada expedición.',
    heroChecks: ['Firma electrónica avanzada', 'API e integraciones', 'Conservación con evidencias'],
    lifeEyebrow: 'Ciclo de vida',
    lifeTitle: 'Del primer dato al archivo',
    lifeText: 'Cada documento recorre el mismo camino, desde el ordenador de la oficina hasta el móvil del conductor, y cada paso queda registrado con su evidencia.',
    steps: [
      { title: 'Creación', text: 'En segundos, desde el ordenador o el móvil, a mano o desde sus sistemas.', items: [
        'Formularios que validan los datos obligatorios',
        'Plantillas por cliente y ruta',
        'Importación de cualquier fichero, también PDF, o creación desde su TMS o ERP',
      ] },
      { title: 'Envío', text: 'Cada participante recibe un acceso único y revocable, sin necesidad de tener cuenta.', items: [
        'Por correo, SMS, WhatsApp, código QR o enlace',
        'Automático al crear o al firmar el documento',
        'Varios documentos a la vez',
      ] },
      { title: 'Firma', text: 'Firma electrónica avanzada conforme al Reglamento eIDAS.', items: [
        'Biométrica manuscrita en el móvil o la tableta',
        'Con sello electrónico, también en procesos automáticos',
        'Verificación con código OTP, si se configura',
      ] },
      { title: 'En ruta', text: 'Lo que pasa durante el transporte queda en el documento, con fecha, hora y autor.', items: [
        'Incidencias y reservas con fotografías',
        'Cambios de matrícula o conductor, trasvases y transferencias',
        'Consulta en inspección, también sin cobertura',
      ] },
      { title: 'Cierre', text: 'Al terminar el transporte, el documento se cierra con todo lo ocurrido.', items: [
        'Firmas, eventos y estado final en el PDF',
        'Cerrado a cambios, con el historial de cada acción',
      ] },
      { title: 'Conservación', text: 'El documento firmado se conserva con sus evidencias y se puede verificar durante años.', items: [
        'PDF en PAdES-LTV: cualquier alteración se detecta',
        'Descarga, exportación y traspaso a otros sistemas',
      ] },
    ],
    docsEyebrow: 'Documentos',
    docsTitle: 'Todos sus documentos, en el mismo sitio',
    docsText: 'Cada documento se genera con datos estructurados, no solo como un PDF, preparado para el intercambio electrónico de información que prevé el marco europeo eFTI.',
    docs: [
      { title: 'eCMR', text: 'Transporte internacional.', to: '/documentos/ecmr' },
      { title: 'Carta de porte', text: 'Transporte nacional: contrato, DeCA y prueba de entrega.', to: '/documentos/carta-porte' },
      { title: 'DeCA', text: 'El documento de control del transporte nacional.', to: '/documentos/documento-control' },
      { title: 'Documento de transporte ADR', text: 'Mercancías peligrosas, junto al DeCA.', to: '/documentos/transporte-adr' },
    ],
    docsMore: 'Y además: lettre de voiture, albarán de entrega, documento de identificación de residuos y documentos a medida para su sector.',
    archEyebrow: 'Archivo',
    archTitle: 'También los documentos que le entregan otros',
    archText: 'El DeCA, el CMR, el albarán o la factura que le da otra empresa se guardan junto a los suyos. La IA reconoce cada documento y extrae sus datos: cargador, transportista, ruta, fechas y referencias.',
    arch: [
      { title: 'Entra como le venga mejor', text: 'Con el QR, el enlace, subiendo varios PDF a la vez o reenviando el correo.' },
      { title: 'Se ordena solo', text: 'Carpetas por cliente, transportista, mes de carga, referencia o tipo.' },
      { title: 'Lo encuentra al momento', text: 'Búsqueda en todo el archivo y descarga en ZIP para una auditoría o una reclamación.' },
      { title: 'Los suyos, también', text: 'A los 30 días del transporte, cerrados a cambios y con las mismas evidencias.' },
    ],
    adaptEyebrow: 'A su medida',
    adaptTitle: 'Se adapta a su operativa, no al revés',
    adapt: [
      { title: 'Sus documentos', text: 'Qué modelos usa y qué campos lleva cada uno, con su logotipo y el PDF en varios idiomas.' },
      { title: 'Su flujo y sus avisos', text: 'Quién firma, en qué orden, por qué canal y con qué verificación, y a quién se avisa de cada firma o incidencia.' },
      { title: 'Su equipo y sus centros', text: 'Perfiles por función, varias delegaciones, doble factor e inicio de sesión único con Microsoft Entra ID.' },
      { title: 'Su marca', text: 'Marca blanca con dominio, logotipo y colores propios, y visor embebido en su web.' },
    ],
    intTitle: 'Conectada con sus sistemas',
    intText: 'Para que los documentos salgan solos de su TMS o ERP y el estado de cada uno vuelva a sus sistemas sin teclear nada.',
    intItems: [
      'API REST y webhooks con cada evento en tiempo real',
      'Enlaces embebidos para firmar desde sus apps, PDA o portal',
      'Sin programar: ficheros en cualquier formato, también PDF, por FTP o por correo',
    ],
    intLink: 'Integraciones',
    trustTitle: 'Con validez jurídica y seguridad',
    trustText: 'Firma electrónica avanzada conforme al Reglamento eIDAS. BLUECMR, S.L.U. dispone de un sistema de gestión certificado de acuerdo al RD 311/2022 (ENS) por OCA CERT.',
    trustItems: [
      'Datos alojados en la Unión Europea',
      'Cifrado en tránsito y en reposo',
      'Registro completo de actividad',
    ],
    trustLink: 'Cumplimiento normativo',
    closing: 'Véala funcionando con sus propios documentos',
    closingText: 'Una demostración con su operativa real, sin compromiso. Después, le acompañamos en la puesta en marcha y, si mueve grandes volúmenes, en un piloto conectado a su TMS o ERP.',
    breadcrumb: ['Inicio', 'Plataforma'],
  },
  en: {
    seoTitle: 'Digital transport documents platform: how it works | BlueCMR',
    seoDescription: 'How BlueCMR manages the lifecycle of your transport documents: creation, sending, electronic signature, events on the road, closing and preservation with evidence.',
    seoKeywords: 'transport documents platform, ecmr platform, electronic consignment note, electronic signature transport, tms erp integration',
    heroEyebrow: 'Platform',
    heroTitle: 'One platform for all your transport documents',
    heroText: 'Issue, share, sign and preserve eCMRs, consignment notes, control documents and ADR documents in one place, connected to your TMS or ERP and to everyone involved in each shipment.',
    heroChecks: ['Advanced electronic signature', 'API and integrations', 'Preservation with evidence'],
    lifeEyebrow: 'Lifecycle',
    lifeTitle: 'From the first data to the archive',
    lifeText: 'Every document follows the same path, from the office computer to the driver\'s phone, and every step is recorded with its evidence.',
    steps: [
      { title: 'Creation', text: 'In seconds, from a computer or a phone, by hand or from your systems.', items: [
        'Forms that validate the mandatory data',
        'Templates per customer and route',
        'Import of any file, PDF included, or creation from your TMS or ERP',
      ] },
      { title: 'Sending', text: 'Each participant gets a unique, revocable access, without needing an account.', items: [
        'By email, SMS, WhatsApp, QR code or link',
        'Automatically when the document is created or signed',
        'Several documents at once',
      ] },
      { title: 'Signature', text: 'Advanced electronic signature under the eIDAS Regulation.', items: [
        'Handwritten biometric on a phone or tablet',
        'With an electronic seal, for automated processes too',
        'OTP verification, if configured',
      ] },
      { title: 'On the road', text: 'What happens during transport is recorded in the document, with date, time and author.', items: [
        'Incidents and reservations with photographs',
        'Plate or driver changes, transhipments and transfers',
        'Shown at roadside checks, even without coverage',
      ] },
      { title: 'Closing', text: 'When transport ends, the document is closed with everything that happened.', items: [
        'Signatures, events and final status in the PDF',
        'Closed to changes, with the history of every action',
      ] },
      { title: 'Preservation', text: 'The signed document is preserved with its evidence and can be verified for years.', items: [
        'PAdES-LTV PDF: any alteration is detected',
        'Download, export and transfer to other systems',
      ] },
    ],
    docsEyebrow: 'Documents',
    docsTitle: 'All your documents, in one place',
    docsText: 'Every document is generated as structured data, not just a PDF, ready for the electronic exchange of information foreseen by the European eFTI framework.',
    docs: [
      { title: 'eCMR', text: 'International transport.', to: '/en/documents/ecmr' },
      { title: 'Consignment note (Spain)', text: 'Domestic transport: contract, control document and proof of delivery.', to: '/en/documents/road-waybill' },
      { title: 'Control document (DeCA)', text: 'The control document for domestic transport in Spain.' },
      { title: 'ADR transport document', text: 'Dangerous goods, together with the control document.' },
    ],
    docsMore: 'And also: French lettre de voiture, delivery note, waste identification document and custom documents for your sector.',
    archEyebrow: 'Archive',
    archTitle: 'Also the documents others hand you',
    archText: 'The control document, CMR, delivery note or invoice another company gives you is stored alongside your own. AI recognises each document and extracts its data: shipper, carrier, route, dates and references.',
    arch: [
      { title: 'In whichever way suits you', text: 'With the QR code, the link, uploading several PDFs at once or forwarding the email.' },
      { title: 'It sorts itself', text: 'Folders by customer, carrier, loading month, reference or type.' },
      { title: 'Found in a moment', text: 'Search across the whole archive and ZIP download for an audit or a claim.' },
      { title: 'Yours too', text: '30 days after transport, closed to changes and with the same evidence.' },
    ],
    adaptEyebrow: 'Tailored',
    adaptTitle: 'It adapts to your operations, not the other way round',
    adapt: [
      { title: 'Your documents', text: 'Which models you use and which fields each one includes, with your logo and the PDF in several languages.' },
      { title: 'Your flow and notifications', text: 'Who signs, in what order, through which channel and with what verification, and who is notified of each signature or incident.' },
      { title: 'Your team and sites', text: 'Role-based profiles, several branches, two-factor authentication and single sign-on with Microsoft Entra ID.' },
      { title: 'Your brand', text: 'White label with your own domain, logo and colours, and an embedded viewer on your website.' },
    ],
    intTitle: 'Connected to your systems',
    intText: 'So that documents are generated automatically from your TMS or ERP and the status of each one flows back into your systems without typing anything.',
    intItems: [
      'REST API and webhooks with every event in real time',
      'Embedded links to sign from your apps, handhelds or portal',
      'No coding: files in any format, PDF included, over FTP or by email',
    ],
    intLink: 'Integrations',
    trustTitle: 'With legal validity and security',
    trustText: 'Advanced electronic signature under the eIDAS Regulation. BLUECMR, S.L.U. holds a management system certified under Royal Decree 311/2022 (ENS) by OCA CERT.',
    trustItems: [
      'Data hosted in the European Union',
      'Encryption in transit and at rest',
      'Full activity log',
    ],
    trustLink: 'Regulatory compliance',
    closing: 'See it working with your own documents',
    closingText: 'A demonstration with your real operations, with no commitment. Then we help you get started and, if you handle large volumes, run a pilot connected to your TMS or ERP.',
    breadcrumb: ['Home', 'Platform'],
  },
};

const PlataformaPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const c = COPY[lang];
  const { getLocalizedUrl } = useLocalizedUrl();
  const primaryCta = usePrimaryCta(lang);

  return (
    <>
      <SeoMeta
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={PAGE_URL[lang]}
        type="website"
        alternates={{ es: PAGE_URL.es, en: PAGE_URL.en, 'x-default': PAGE_URL.es }}
        keywords={c.seoKeywords}
        breadcrumbs={[
          { name: c.breadcrumb[0], url: lang === 'es' ? 'https://www.bluecmr.com/' : 'https://www.bluecmr.com/en' },
          { name: c.breadcrumb[1], url: PAGE_URL[lang] }
        ]}
        structuredData={[
          { '@context': 'https://schema.org', '@type': 'WebPage', name: c.heroTitle, description: c.seoDescription, url: PAGE_URL[lang] },
        ]}
      />

      <div className="bg-white">
        <PageHero
          eyebrow={c.heroEyebrow}
          title={c.heroTitle}
          text={c.heroText}
          image={HERO_IMAGE}
          primary={primaryCta}
          compact
        >
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            {c.heroChecks.map((item) => (
              <li key={item} className="flex items-center gap-2 text-slate-200">
                <Check className="w-4 h-4 text-blue-300" strokeWidth={2.5} />
                {item}
              </li>
            ))}
          </ul>
        </PageHero>

        {/* Logos de clientes: siempre en B/N y pequeños (condición de su permiso) */}
        <ClientLogos title={lang === 'es' ? 'Algunas de las empresas que trabajan con BlueCMR' : 'Some of the companies working with BlueCMR'} />

        {/* Ciclo de vida */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionIntro eyebrow={c.lifeEyebrow} title={c.lifeTitle} text={c.lifeText} />
            <ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14">
              {c.steps.map((step, i) => {
                const Icon = STEP_ICONS[i];
                return (
                  <li key={step.title} className="border-t-2 border-slate-900 pt-6">
                    <div className="flex items-center gap-3">
                      <Icon className="w-6 h-6 text-primary-700" strokeWidth={1.5} />
                      <span className="text-sm font-semibold text-primary-700 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                    </div>
                    <h3 className="mt-4 text-xl font-semibold text-slate-900">{step.title}</h3>
                    <p className="mt-2 text-slate-700 leading-relaxed">{step.text}</p>
                    <CheckList items={step.items} />
                  </li>
                );
              })}
            </ol>
          </div>
        </section>

        {/* Documentos */}
        <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <SectionIntro eyebrow={c.docsEyebrow} title={c.docsTitle} text={c.docsText} className="" />
              <p className="mt-6 text-slate-600 leading-relaxed">{c.docsMore}</p>
            </div>
            <ul className="lg:col-span-7 border-b border-slate-300">
              {c.docs.map((d) => {
                const body = (
                  <>
                    <span>
                      <span className="block font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">{d.title}</span>
                      <span className="mt-1 block text-sm text-slate-600">{d.text}</span>
                    </span>
                    {d.to && <ArrowRight className="mt-1 w-5 h-5 flex-shrink-0 text-slate-400 group-hover:text-primary-700 group-hover:translate-x-1 transition" />}
                  </>
                );
                return (
                  <li key={d.title} className="border-t border-slate-300">
                    {d.to
                      ? <Link to={d.to} className="group flex items-start justify-between gap-4 py-5">{body}</Link>
                      : <div className="flex items-start justify-between gap-4 py-5">{body}</div>}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* Archivo de documentos de otras empresas */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <SectionIntro eyebrow={c.archEyebrow} title={c.archTitle} text={c.archText} className="" />
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-x-10 gap-y-10">
              {c.arch.map((item) => (
                <div key={item.title} className="border-t border-slate-300 pt-6">
                  <h3 className="font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Se adapta */}
        <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionIntro eyebrow={c.adaptEyebrow} title={c.adaptTitle} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-10">
              {c.adapt.map((item) => (
                <div key={item.title} className="border-t border-slate-300 pt-6">
                  <h3 className="font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Integración y garantías */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-x-16 gap-y-14">
            {[
              { icon: Plug, title: c.intTitle, text: c.intText, items: c.intItems, link: c.intLink, to: getLocalizedUrl('/interoperabilidad') },
              { icon: ShieldCheck, title: c.trustTitle, text: c.trustText, items: c.trustItems, link: c.trustLink, to: getLocalizedUrl('/confianza/cumplimiento-normativo') },
            ].map((block) => {
              const Icon = block.icon;
              return (
                <div key={block.title} className="border-t-2 border-slate-900 pt-6">
                  <Icon className="w-7 h-7 text-primary-700 mb-5" strokeWidth={1.5} />
                  <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{block.title}</h2>
                  <p className="mt-4 text-slate-600 leading-relaxed">{block.text}</p>
                  <CheckList items={block.items} className="mt-6 space-y-2.5" />
                  <Link to={block.to} className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:gap-3 transition-all">
                    {block.link} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </section>

        <ClosingCta title={c.closing} text={c.closingText} lang={lang} />
      </div>
    </>
  );
};

export default PlataformaPage;
